import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, type Paginated } from '@/lib/api';

export interface Purchase {
  id: number;
  wine_id: number;
  supplier_id: number | null;
  supplier_text: string | null;
  purchase_date: string;
  quantity: number;
  unit_price: number | null;
  currency: string;
  exchange_rate: number | null;
  unit_price_usd: number | null;
  acquisition_type: 'purchase' | 'gift' | 'promo' | 'sample' | 'own_production';
  notes: string | null;
  wine?: { id: number; name: string; vintage: number | null };
  supplier?: { id: number; name: string } | null;
  created_at: string;
}

export function useWinePurchases(wineId: number | undefined) {
  return useQuery({
    enabled: !!wineId,
    queryKey: ['purchases', { wine_id: wineId }],
    queryFn: async () => {
      const { data } = await api.get<Paginated<Purchase>>('/purchases', {
        params: { wine_id: wineId, per_page: 100 },
      });
      return data.data;
    },
  });
}

export function useUpdatePurchase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: number; patch: Partial<Purchase> }) => {
      const { data } = await api.put<{ data: Purchase }>(`/purchases/${id}`, patch);
      return data.data;
    },
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ['purchases', { wine_id: p.wine_id }] });
      qc.invalidateQueries({ queryKey: ['wine', p.wine_id] });
      qc.invalidateQueries({ queryKey: ['wines'] });
    },
  });
}

export function useDeletePurchase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: { id: number; wineId: number }) => {
      await api.delete(`/purchases/${id}`);
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['purchases', { wine_id: vars.wineId }] });
      qc.invalidateQueries({ queryKey: ['wine', vars.wineId] });
      qc.invalidateQueries({ queryKey: ['wines'] });
    },
  });
}
